import React, { useContext, useEffect, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlbumSelect } from '@features/AlbumSelect';
import { showToast } from '@features/ToastManager';
import { CustomModal, UiButton } from '@shared/ui';
import { OptionType } from '@shared/ui/MultiSelect';

import cls from './MassAddInAlbumModal.module.css';
import { useAppDispatch } from '../../../store';
import { massAddingInAlbums } from '../../../store/slices/dashboardSlice/action';
import {
    dashboardActions,
    progressSelector,
} from '../../../store/slices/dashboardSlice/dashboardSlice';
import { ProgressContext } from '../../../TemporaryConmonents/DashboardInformation/Context/ProgressContext';
import { ConstructionProgress } from '../../../TemporaryConmonents/DashboardInformation/types/dashboardInformation';

interface MassAddInAlbumModalProps {
    classes?: string;
    onClose?: () => void;
    isOpen: boolean;
}

export const MassAddInAlbumModal = ({ classes, onClose, isOpen }: MassAddInAlbumModalProps) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const [selectedAlbums, setSelectedAlbums] = useState<OptionType[]>([]);

    const mediaListEntities = useSelector(progressSelector.selectEntities);

    const { selectedMassMediaIds, activeMassSelectModeHandle } = useContext(ProgressContext);

    useEffect(() => {
        const selectedMedia = selectedMassMediaIds.map((mediaId) => mediaListEntities[mediaId]);

        const getCommonAlbumIds: string[] = (files: ConstructionProgress[]): string[] => {
            if (!files.length) return [];

            return files.reduce((acc: string[], file, index) => {
                const ids = new Set(file.albums.map((a) => a.id));
                return index === 0 ? [...ids] : acc.filter((id) => ids.has(id));
            }, []);
        };

        const commonAlbumIds = getCommonAlbumIds(selectedMedia);

        const selectedOptions = albums.filter((album) => commonAlbumIds.includes(album.value));

        setSelectedAlbums(selectedOptions);
    }, []);

    // @ts-ignore
    const { albums, temporalAlbums } = useSelector((state) => state.dashboardReducer);

    const onAddNewAlbum = (album: OptionType) => {
        dispatch(dashboardActions.addAlbums(album));
        setSelectedAlbums((prevState) => [...prevState, album]);
    };

    const deleteSelectedAlbum = (value: string) => {
        const updatedAlbums = selectedAlbums.filter(
            (album) => String(album.value) !== String(value)
        );

        setSelectedAlbums(updatedAlbums);
    };

    const onChangeAlbum = (value: string) => {
        const isFoundAlbumSelected = selectedAlbums.some(
            (album) => String(album.value) === String(value)
        );

        const updatedAlbums = selectedAlbums.filter((album) => {
            if (isFoundAlbumSelected) {
                return String(album.value) !== String(value);
            }
            return true;
        });

        // @ts-ignore
        const newAlbum = [...albums, ...temporalAlbums].find(
            (album) => String(album.value) === String(value)
        );

        setSelectedAlbums([...updatedAlbums, newAlbum]);
    };

    const onSubmit = () => {
        const selectedAlbumsTitles = selectedAlbums.map((album) => album.title);

        let isCreatedAlbum = false;

        temporalAlbums.forEach((album) => {
            selectedAlbumsTitles.includes(album.title)
                ? (isCreatedAlbum = true)
                : (isCreatedAlbum = false);
        });

        dispatch(
            // @ts-ignore
            massAddingInAlbums({
                albums: selectedAlbums.map((album) => album.title),
                media_ids: selectedMassMediaIds,
            })
        );
        showToast(
            isCreatedAlbum
                ? t('MassAddInAlbumModal.onSubmit.showToast.created')
                : t('MassAddInAlbumModal.onSubmit.showToast')
        );
        activeMassSelectModeHandle(false);
        onClose();
    };

    return (
        <CustomModal
            onHideModal={onClose}
            isOpen={isOpen}
            contentClasses={cn(cls.MassAddInAlbumModal, [classes])}
        >
            <CustomModal.Header>
                {t('MassAddInAlbumModal.return.CustomModal.Header')}
            </CustomModal.Header>
            <CustomModal.Body>
                <AlbumSelect
                    title={t('MassAddInAlbumModal.return.CustomModal.Body.AlbumSelect.title')}
                    selected={selectedAlbums}
                    options={[...albums, ...temporalAlbums]}
                    deleteHandle={deleteSelectedAlbum}
                    onChange={onChangeAlbum}
                    onAddNewTag={onAddNewAlbum}
                />
            </CustomModal.Body>
            <CustomModal.Footer classes={cls.modalFooter}>
                <UiButton
                    onClick={onClose}
                    btnColor="secondary"
                    btnText={t('button.cancel.text')}
                />
                <UiButton onClick={onSubmit} btnText={t('button.save.text')} />
            </CustomModal.Footer>
        </CustomModal>
    );
};
